import React, { useCallback, useEffect } from "react";
import { Outlet } from "react-router-dom";

import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { useAppSelector } from "app/hooks";
import notificationSound from "assets/audio/notification-sound.mp3";
import AlertBar from "components/alert-bar";
import { useNeonNow } from "components/neon-now/use-neon-now.hook";
import { APPOINTMENT_STATUS_ARRIVED } from "constants/appointments";
import { selectCurrentUser } from "features/auth/auth-slice";
import CallLogDrawer from "features/calls/call-log-drawer/call-log-drawer.component";
import PatientDrawer, {
  usePatientDrawer
} from "features/patients/patient-drawer";
import PatientNotesDrawer from "features/patients/patient-notes-drawer";
import StaffTasksV2Drawer from "features/staff-tasks-v2/drawer/staff-tasks-v2-drawer.component";
import useAlertBar from "hooks/use-alert-bar.hook";
import useClinicNotifications, {
  AppointmentStatusUpdateType
} from "hooks/use-clinic-notifications";
import { ContainerSizeProvider } from "hooks/use-container-size.hook";
import useIsMobile from "hooks/use-is-mobile.hook";
import { usePatientPrivacy } from "hooks/use-patient-privacy.hook";
import { Patient } from "services/types/patients.type";

import LayoutTopBar from "../layout-top-bar";
import PracticeContextBanner from "../practice-context-banner/practice-context-banner.component";

type LayoutMainContentProps = {
  isDrawerOpen: boolean;
};

export default function LayoutMainContent({
  isDrawerOpen
}: LayoutMainContentProps) {
  const { addMessageHandlers, removeMessageHandlers } =
    useClinicNotifications();
  const { addAlert, alerts, removeAlert } = useAlertBar();
  const currentUser = useAppSelector(selectCurrentUser);
  const isMobile = useIsMobile();

  const checkAppointmentPatientArrived = useCallback(
    (message: any) => {
      if (message?.type === AppointmentStatusUpdateType) {
        const {
          id,
          staff_member,
          new_status,
          patient,
          type,
          supervising_provider,
          is_direct_supervisor
        } = message.message;

        const isForPrimaryProvider = staff_member?.id === currentUser.id;
        const isForDirectSupervisor =
          Boolean(currentUser.supervising_provider_notifications_enabled) &&
          is_direct_supervisor === true &&
          supervising_provider === currentUser.id;

        if (
          new_status === APPOINTMENT_STATUS_ARRIVED &&
          (isForPrimaryProvider || isForDirectSupervisor)
        ) {
          addAlert({
            message: (
              <AppointmentPatientArrivedMessage patient={patient} type={type} />
            ),
            severity: "warning",
            id
          });
        }
      }
    },

    [currentUser, addAlert]
  );

  useEffect(() => {
    const handlerKey = "appointment-arrive";
    addMessageHandlers(checkAppointmentPatientArrived, handlerKey);
    return () => removeMessageHandlers(handlerKey);
  }, [
    checkAppointmentPatientArrived,
    addMessageHandlers,
    removeMessageHandlers
  ]);

  const { isOpen, rightOffset } = usePatientDrawer();
  const { rightOffset: neonNowDrawerOffset } = useNeonNow();

  return (
    <Stack width={isMobile ? "100%" : "calc(100% - 100px)"}>
      <PracticeContextBanner />
      <LayoutTopBar />
      {alerts.length > 0 && (
        <AlertBar alerts={alerts} removeAlert={removeAlert} />
      )}
      <ContainerSizeProvider
        sx={{
          width: `calc(100% - ${rightOffset + neonNowDrawerOffset}px)`,
          boxSizing: "border-box",
          overflow: "hidden",
          flex: 1,
          display: "flex"
        }}
      >
        <Outlet />
        {/* when isOpen==false we have to unmount PatientDrawer because it is using AppointmentsProvider inside */}
        {isOpen && <PatientDrawer />}
        <CallLogDrawer />
        <PatientNotesDrawer />
        <StaffTasksV2Drawer />
      </ContainerSizeProvider>
    </Stack>
  );
}

const AppointmentPatientArrivedMessage = ({
  patient,
  type
}: {
  patient: Patient;
  type: any;
}) => {
  const theme = useTheme();
  const currentUser = useAppSelector(selectCurrentUser);

  useEffect(() => {
    if (currentUser?.patient_arrived_sound_enabled) {
      const audio = new Audio(notificationSound);
      audio.play().catch(() => {
        // Autoplay blocked by browser - user needs to interact with page first
      });
    }
  }, [currentUser?.patient_arrived_sound_enabled]);

  const { isPrivateMode } = usePatientPrivacy();

  const patientName = (
    isPrivateMode
      ? [patient.first_name?.[0], patient.last_name?.[0]]
          .filter(Boolean)
          .join(".")
      : [patient.first_name, patient.last_name].join(" ")
  ).trim();

  return (
    <Link
      display="flex"
      href={`/patients/${patient.id}/`}
      color={theme.palette.common.white}
      underline="hover"
      target="_blank"
      gap={1}
    >
      <Typography fontSize="inherit">{`Patient ${patientName} has arrived for ${type.name}`}</Typography>
      <Typography fontSize="inherit">-</Typography>
      <Typography fontSize="inherit">Open Patient Chart</Typography>
    </Link>
  );
};
